import React, {useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {getFallList} from "../../../selectors/animalDocumentsSelectors";
import {localMomentToUTC} from "../../../utils/DateTimeUtils";
import useAnimalGroupInfo from "../ideal-charts/useAnimalGroupInfo";
import useDomain from "../ideal-charts/useDomain";
import useReferenceLines from "../ideal-charts/useReferenceLines";
import {getFallReasons} from "../../../selectors/dictionarySelectors";
import FallChart from "./FallChart";
import TextSwitch from "../../basics/text-switch/TextSwitch";
import CollapsableContainer from "../containers/CollapsableContainer";
import {useTranslation} from "react-i18next";
import FallHistogram from "../ideal-charts/charts/FallHistogram";

const FallGroupChart = () => {
    const {t} = useTranslation();

    const [chartType, setChartType] = useState("chart");

    const groupInfo = useAnimalGroupInfo();

    const fallEvents = useSelector(getFallList);

    const fallReasons = useSelector(getFallReasons);

    const domain = useDomain();

    const referenceLines = useReferenceLines(domain, "lineAxis");

    const fallDict = useMemo(() => {
        const dictionary = {};
        for (const event of fallEvents) {
            const utcTIME = +localMomentToUTC(event?.EvTime).startOf("day");
            if (!dictionary[utcTIME]) dictionary[utcTIME] = [];
            const reason =
                fallReasons.WData.find(
                    (item) => item.ID === event?.EvData?.Reasn
                )?.Value || event?.EvData?.Reasn;

            dictionary[utcTIME].push({
                [reason]: event?.EvData?.Weight || 0,
                weight: event?.EvData?.Weight || 0,
                AnmCnt: event?.EvData?.AnmCnt || event?.AnmCnt || 1,
                PlcmntID: event?.EvData?.PlcmntID || event?.PlcmntID || null,
                Reason: reason,
                AnmID: event?.AnmID,
            });
        }
        return dictionary;
    }, [fallEvents, fallReasons]);

    const options = useMemo(
        () => [
            {
                name: (
                    <>
                        <i className="fa-solid fa-chart-scatter me-2" />
                        <span>{t("chart")}</span>
                    </>
                ),
                value: "chart",
            },
            {
                name: (
                    <>
                        <i className="fa-solid fa-chart-column me-2" />
                        <span>{t("chamber.chart.hist")}</span>
                    </>
                ),
                value: "histogram",
            },
        ],
        [t]
    );

    return (
        <CollapsableContainer.Card
            id="cy-falls-card"
            header={t("printSelectedAnimalsModal.falls")}
            defaultExpanded={fallEvents.length}>
            <TextSwitch
                options={options}
                value={chartType}
                onChange={setChartType}
            />
            {chartType === "chart" && (
                <FallChart
                    fallDict={fallDict}
                    chartDomain={domain}
                    referenceLines={referenceLines}
                    {...groupInfo}
                />
            )}
            {chartType === "histogram" && <FallHistogram />}
        </CollapsableContainer.Card>
    );
};

export default React.memo(FallGroupChart);
